import { StatisticType } from "../types/dashboard.type";
import { OrganizationType } from "../types/organization.type";
import type { MetricValueKind } from "../utils/numberFormat";

const ALL_ORG_TYPES: OrganizationType[] = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
  OrganizationType.CHARITY,
];

/** Waste Outlet + Charity (site/container operations, no financial exclusives). */
const SITE_OPS_TYPES: OrganizationType[] = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.CHARITY,
];

/** Organisations whose pickups are collected from their own sites or fleet. */
const COLLECTING_TYPES: OrganizationType[] = [...SITE_OPS_TYPES, OrganizationType.TRUCK];

export interface DashboardStatDescriptor {
  stat: StatisticType;
  /** Org types this metric is relevant for. */
  allowedTypes: OrganizationType[];
  /** Pre-selected by default for its allowed types. */
  isDefault?: boolean;
  /**
   * False when the API still returns a hard-coded 0 / empty list for this
   * field. Unavailable stats are never offered, selected or shown — a fake 0
   * is worse than no figure. Stored selections drop them automatically.
   */
  available?: false;
}

/** Dashboard metric catalog — org-type gating (mirrors ZERO_NAV_CATALOG). */
export const DASHBOARD_STAT_CATALOG: DashboardStatDescriptor[] = [
  // ---- Orders (every organisation type) ----
  { stat: StatisticType.TOTAL_ORDERS, allowedTypes: ALL_ORG_TYPES, isDefault: true },
  { stat: StatisticType.COMPLETION_RATE, allowedTypes: ALL_ORG_TYPES, isDefault: true },
  { stat: StatisticType.CANCELLATION_RATE, allowedTypes: ALL_ORG_TYPES },
  { stat: StatisticType.AVERAGE_CONTAINER_FILL_RATE, allowedTypes: ALL_ORG_TYPES },
  { stat: StatisticType.TRIPS_TODAY_COUNT, allowedTypes: ALL_ORG_TYPES },

  // ---- Waste quantities ----
  { stat: StatisticType.COMPLETED_WASTE_AMOUNT, allowedTypes: COLLECTING_TYPES, isDefault: true },
  { stat: StatisticType.TOTAL_WASTE_AMOUNT, allowedTypes: COLLECTING_TYPES },
  // Recycler wording for the same quantities: received (completed) / requested (all).
  { stat: StatisticType.SUSTAINABLE_SUPPLY, allowedTypes: [OrganizationType.RECYCLER], isDefault: true },
  { stat: StatisticType.RECYCLER_APPETITE, allowedTypes: [OrganizationType.RECYCLER], isDefault: true },
  { stat: StatisticType.WASTE_DIVERSION_RATE, allowedTypes: ALL_ORG_TYPES, isDefault: true },

  // ---- Material intelligence ----
  { stat: StatisticType.WASTE_TYPE_BREAKDOWN, allowedTypes: ALL_ORG_TYPES, isDefault: true },
  { stat: StatisticType.INCOMING_WASTE_BREAKDOWN, allowedTypes: ALL_ORG_TYPES, isDefault: true },

  // ---- Containers (site operations) ----
  { stat: StatisticType.FULL_PERCENTAGE, allowedTypes: SITE_OPS_TYPES, isDefault: true },

  // ---- Financial ----
  // Revenue is only for commercial waste producers (not Charity, transporters, or recyclers).
  { stat: StatisticType.TOTAL_REVENUE, allowedTypes: [OrganizationType.WASTE_OUTLET], isDefault: true },
  {
    stat: StatisticType.AVERAGE_INCENTIVE_PER_KG,
    allowedTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.RECYCLER],
  },

  // ---- Not calculated by the API yet (hard-coded 0 / []) ----
  { stat: StatisticType.STORAGE_CAPACITY_UTILIZATION, allowedTypes: SITE_OPS_TYPES, available: false },
  { stat: StatisticType.EMERGENCY_COLLECTION_REQUESTS, allowedTypes: SITE_OPS_TYPES, available: false },
  { stat: StatisticType.WASTE_COMPACTION_EFFICIENCY, allowedTypes: SITE_OPS_TYPES, available: false },
  { stat: StatisticType.TRUCK_DRIVER_COUNT, allowedTypes: [OrganizationType.TRUCK], available: false },
  { stat: StatisticType.AVERAGE_ROUTE_EFFICIENCY, allowedTypes: [OrganizationType.TRUCK], available: false },
  { stat: StatisticType.FUEL_CONSUMPTION_PER_TRIP, allowedTypes: [OrganizationType.TRUCK], available: false },
  { stat: StatisticType.DRIVER_PERFORMANCE_SCORE, allowedTypes: [OrganizationType.TRUCK], available: false },
  { stat: StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE, allowedTypes: [OrganizationType.TRUCK], available: false },
  { stat: StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY, allowedTypes: [OrganizationType.TRUCK], available: false },
  { stat: StatisticType.SUPPLY_PER_RECYCLER, allowedTypes: [OrganizationType.RECYCLER], available: false },
  { stat: StatisticType.PROCESSING_CAPACITY_UTILIZATION, allowedTypes: [OrganizationType.RECYCLER], available: false },
  { stat: StatisticType.QUALITY_COMPLIANCE_RATE, allowedTypes: [OrganizationType.RECYCLER], available: false },
  { stat: StatisticType.REJECTED_WASTE_PERCENTAGE, allowedTypes: [OrganizationType.RECYCLER], available: false },
  { stat: StatisticType.AVERAGE_PROCESSING_TIME, allowedTypes: [OrganizationType.RECYCLER], available: false },
  {
    stat: StatisticType.CUSTOMER_SATISFACTION_SCORE,
    allowedTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    available: false,
  },
  { stat: StatisticType.COLLECTION_FREQUENCY, allowedTypes: COLLECTING_TYPES, available: false },
  { stat: StatisticType.TIME_FROM_FULL_TO_COLLECTION, allowedTypes: COLLECTING_TYPES, available: false },
  {
    stat: StatisticType.WASTE_QUALITY_RATING,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.RECYCLER],
    available: false,
  },
];

const AVAILABLE_CATALOG = DASHBOARD_STAT_CATALOG.filter((d) => d.available !== false);

/** Stats the API currently fills with a hard-coded 0 / empty list. */
export const UNAVAILABLE_STATISTIC_TYPES: ReadonlySet<StatisticType> = new Set(
  DASHBOARD_STAT_CATALOG.filter((d) => d.available === false).map((d) => d.stat),
);

export function isStatAvailable(stat: string): boolean {
  return !UNAVAILABLE_STATISTIC_TYPES.has(stat as StatisticType);
}

export function getStatsForOrgType(orgType: OrganizationType): StatisticType[] {
  return AVAILABLE_CATALOG
    .filter((d) => d.allowedTypes.includes(orgType))
    .map((d) => d.stat);
}

export function getDefaultStatsForOrgType(orgType: OrganizationType): StatisticType[] {
  return AVAILABLE_CATALOG
    .filter((d) => d.isDefault && d.allowedTypes.includes(orgType))
    .map((d) => d.stat);
}

export function isStatAllowedForOrgType(
  stat: StatisticType,
  orgType: OrganizationType
): boolean {
  return AVAILABLE_CATALOG.some(
    (d) => d.stat === stat && d.allowedTypes.includes(orgType)
  );
}

/** Stats whose value is a list (charts / maps) rather than a single number. */
export const LIST_STATISTIC_TYPES: ReadonlySet<StatisticType> = new Set([
  StatisticType.INCOMING_WASTE_BREAKDOWN,
  StatisticType.WASTE_TYPE_BREAKDOWN,
  StatisticType.SUPPLY_PER_RECYCLER,
  StatisticType.ACTIVE_TRUCKS,
]);

/** Stats with a Charity-specific wording (`dashboard.statsCharity.<key>`). */
const CHARITY_TITLE_STATS = new Set<StatisticType>([
  StatisticType.INCOMING_WASTE_BREAKDOWN,
  StatisticType.WASTE_TYPE_BREAKDOWN,
]);

/** Stats with a Recycler-specific wording (`dashboard.statsRecycler.<key>`). */
const RECYCLER_TITLE_STATS = new Set<StatisticType>([
  StatisticType.AVERAGE_INCENTIVE_PER_KG,
  StatisticType.INCOMING_WASTE_BREAKDOWN,
]);

/**
 * i18n key for a stat's title. Titles are translated in every language; the
 * key (not the English text) is what KPI limits and preferences store.
 */
export function getStatTitleKey(
  statKey: string,
  orgType?: OrganizationType | null
): string {
  if (orgType === OrganizationType.CHARITY && CHARITY_TITLE_STATS.has(statKey as StatisticType)) {
    return `dashboard.statsCharity.${statKey}`;
  }
  if (orgType === OrganizationType.RECYCLER && RECYCLER_TITLE_STATS.has(statKey as StatisticType)) {
    return `dashboard.statsRecycler.${statKey}`;
  }
  return `dashboard.stats.${statKey}`;
}

/** Readable fallback when a title key is missing: `total_orders` → "Total orders". */
export function humanizeStatKey(statKey: string): string {
  const text = statKey.replace(/_/g, " ").trim().toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type Translate = (key: string, options?: { defaultValue?: string }) => string;

/**
 * Translated title for a stat key in any casing (`TOTAL_ORDERS`, legacy
 * `outgoing_waste_breakdown`), e.g. for KPI limits saved by older releases.
 */
export function getStatLabel(
  t: Translate,
  statKey: string,
  orgType?: OrganizationType | null,
): string {
  const [key] = migrateLegacyStatKeys([statKey.trim().toLowerCase()]);
  return t(getStatTitleKey(key, orgType), { defaultValue: humanizeStatKey(key) });
}

/** Display formatting per numeric stat (unit and precision). */
export const STAT_VALUE_KIND: Partial<Record<StatisticType, MetricValueKind>> = {
  [StatisticType.TOTAL_ORDERS]: "count",
  [StatisticType.TRIPS_TODAY_COUNT]: "count",
  [StatisticType.COMPLETION_RATE]: "percent",
  [StatisticType.CANCELLATION_RATE]: "percent",
  [StatisticType.WASTE_DIVERSION_RATE]: "percent",
  [StatisticType.FULL_PERCENTAGE]: "percent",
  [StatisticType.AVERAGE_CONTAINER_FILL_RATE]: "mass_kg",
  [StatisticType.COMPLETED_WASTE_AMOUNT]: "mass_kg",
  [StatisticType.TOTAL_WASTE_AMOUNT]: "mass_kg",
  [StatisticType.SUSTAINABLE_SUPPLY]: "mass_kg",
  [StatisticType.RECYCLER_APPETITE]: "mass_kg",
  [StatisticType.TOTAL_REVENUE]: "sar",
  [StatisticType.AVERAGE_INCENTIVE_PER_KG]: "sar_per_kg",
};

export function getStatValueKind(stat: string): MetricValueKind {
  return STAT_VALUE_KIND[stat as StatisticType] ?? "decimal";
}

/** Live map / KPI sections that are not StatisticType cards. */
export const DASHBOARD_EXTRA_KPI = "kpi_progress";
export const DASHBOARD_EXTRA_MAP = "map";

export type DashboardExtraWidgetId =
  | typeof DASHBOARD_EXTRA_KPI
  | typeof DASHBOARD_EXTRA_MAP;

export type DashboardViewTab = "monthly" | "overview";

export function getDashboardExtrasForOrgTab(
  orgType: OrganizationType,
  tab: DashboardViewTab,
): DashboardExtraWidgetId[] {
  const extras: DashboardExtraWidgetId[] = [DASHBOARD_EXTRA_KPI];
  const showSiteMap =
    tab === "monthly" &&
    (orgType === OrganizationType.WASTE_OUTLET ||
      orgType === OrganizationType.CHARITY);
  // Waste movers and recyclers both run route fleets (route runs + trucks).
  const showFleetMap =
    orgType === OrganizationType.TRUCK || orgType === OrganizationType.RECYCLER;
  if (showSiteMap || showFleetMap) extras.push(DASHBOARD_EXTRA_MAP);
  return extras;
}

/** Payload fields that are not pickable cards (map data, previous-month revenue). */
export const NON_PICKABLE_STATISTIC_TYPES = new Set<StatisticType>([
  StatisticType.ACTIVE_TRUCKS,
  StatisticType.TOTAL_REVENUE_LAST_MONTH,
]);

/** Keys renamed since earlier releases (stored selections / KPI limits). */
const LEGACY_STAT_KEYS: Record<string, StatisticType> = {
  outgoing_waste_breakdown: StatisticType.INCOMING_WASTE_BREAKDOWN,
};

/** Maps legacy stored stat ids to current ones, dropping duplicates. */
export function migrateLegacyStatKeys<T extends string>(ids: readonly T[]): T[] {
  const out: T[] = [];
  for (const id of ids) {
    const next = (LEGACY_STAT_KEYS[id] ?? id) as T;
    if (!out.includes(next)) out.push(next);
  }
  return out;
}
